import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Checkbox,
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
  Divider,
  Radio,
  Switch,
} from "@mantine/core";
import { IconArrowUp, IconArrowDown, IconGridDots } from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface ReviewScreenPreferences {
  editingMode: "grid" | "document";
  postSignOutAction: "return-to-queue" | "next-case" | "scan-modal";
  widgetOrder: string[];
  showQueueInSidebar: boolean;
}

export const REVIEW_SCREEN_PREFERENCE_KEY = "pathologist_review_screen";

export const ALL_REVIEW_WIDGETS = [
  // Default on
  { id: "client-requisition", label: "Client / Requisition", defaultOn: true },
  { id: "icd-codes", label: "ICD Codes", defaultOn: true },
  { id: "cpt-codes", label: "CPT Codes", defaultOn: true },
  { id: "results", label: "Historical Reports", defaultOn: true },
  {
    id: "microscopic-description",
    label: "Microscopic Description",
    defaultOn: true,
  },
  { id: "stains", label: "Stains", defaultOn: true },
  { id: "grossing", label: "Grossing", defaultOn: true },
  {
    id: "documents-files-images",
    label: "Documents",
    defaultOn: true,
  },
  { id: "send-outs", label: "Send Outs", defaultOn: true },
  // Optional (off by default)
  { id: "case-queue", label: "Case Queue", defaultOn: false },
  { id: "panels", label: "Panels", defaultOn: false },
  { id: "qa-review-status", label: "QA Review", defaultOn: false },
  { id: "notes", label: "Notes", defaultOn: false },
  { id: "comments", label: "Comments", defaultOn: false },
  { id: "discussions", label: "Discussions", defaultOn: false },
  { id: "errors", label: "Errors", defaultOn: false },
  { id: "prior-cases", label: "Prior Cases", defaultOn: true },
  { id: "clinical-history", label: "Clinical History", defaultOn: false },
  { id: "colon-diagram", label: "Organ Map", defaultOn: false },
  { id: "flags", label: "Flags", defaultOn: true },
];

export const DEFAULT_REVIEW_PREFERENCES: ReviewScreenPreferences = {
  editingMode: "grid",
  postSignOutAction: "return-to-queue",
  widgetOrder: ALL_REVIEW_WIDGETS.filter((w) => w.defaultOn).map((w) => w.id),
  showQueueInSidebar: true,
};

interface SortableWidgetRowProps {
  id: string;
  label: string;
  checked: boolean;
  index: number;
  total: number;
  animatingId: string | null;
  animatingDirection: "up" | "down" | null;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function SortableWidgetRow({
  id,
  label,
  checked,
  index,
  total,
  animatingId,
  animatingDirection,
  onToggle,
  onMoveUp,
  onMoveDown,
}: SortableWidgetRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const isAnimating = animatingId === id;
  const translateY = isAnimating ? (animatingDirection === "up" ? -36 : 36) : 0;

  const style = {
    transform: isDragging
      ? CSS.Transform.toString(transform)
      : `translateY(${translateY}px)`,
    transition: isDragging ? transition : "transform 180ms ease",
    opacity: isDragging ? 0.4 : 1,
    background: isDragging ? "var(--mantine-color-blue-0)" : undefined,
    borderRadius: 4,
    cursor: "grab",
    touchAction: "none" as const,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <Group
      ref={setNodeRef}
      style={style}
      gap={6}
      wrap="nowrap"
      py={6}
      px={4}
      {...attributes}
      {...listeners}
    >
      <IconGridDots size={14} color="var(--mantine-color-gray-5)" />
      <Checkbox
        checked={checked}
        size="sm"
        onPointerDown={(e) => e.stopPropagation()}
        onChange={() => onToggle()}
      />
      <Text
        size="sm"
        style={{ flex: 1, cursor: "pointer", userSelect: "none" }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {label}
      </Text>
      <Group gap={2} wrap="nowrap">
        <ActionIcon
          variant="subtle"
          size="xs"
          disabled={index === 0}
          title="Move up"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp();
          }}
        >
          <IconArrowUp size={13} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          size="xs"
          disabled={index === total - 1}
          title="Move down"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown();
          }}
        >
          <IconArrowDown size={13} />
        </ActionIcon>
      </Group>
    </Group>
  );
}

interface ReviewPreferencesModalProps {
  opened: boolean;
  onClose: () => void;
  currentPreferences: ReviewScreenPreferences;
  onSave: (prefs: ReviewScreenPreferences) => void;
  isSaving: boolean;
}

export function ReviewPreferencesModal({
  opened,
  onClose,
  currentPreferences,
  onSave,
  isSaving,
}: ReviewPreferencesModalProps) {
  const [editingMode, setEditingMode] = useState<"grid" | "document">("grid");
  const [postSignOutAction, setPostSignOutAction] = useState<
    "return-to-queue" | "next-case" | "scan-modal"
  >("return-to-queue");
  const [showQueueInSidebar, setShowQueueInSidebar] = useState(true);

  // Widget order tracks all widgets; enabled set tracks which are on
  const [widgetOrder, setWidgetOrder] = useState<string[]>([]);
  const [enabledSet, setEnabledSet] = useState<Set<string>>(new Set());

  const [animatingId, setAnimatingId] = useState<string | null>(null);
  const [animatingDirection, setAnimatingDirection] = useState<
    "up" | "down" | null
  >(null);

  const prevOpened = useRef(false);

  useEffect(() => {
    if (opened && !prevOpened.current) {
      setEditingMode(currentPreferences.editingMode);
      setPostSignOutAction(currentPreferences.postSignOutAction);
      setShowQueueInSidebar(currentPreferences.showQueueInSidebar ?? true);

      const enabledIds = currentPreferences.widgetOrder;
      const allIds = ALL_REVIEW_WIDGETS.map((w) => w.id);
      const disabledIds = allIds.filter((id) => !enabledIds.includes(id));
      setWidgetOrder([...enabledIds, ...disabledIds]);
      setEnabledSet(new Set(enabledIds));
    }
    prevOpened.current = opened;
  }, [opened, currentPreferences]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setWidgetOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const toggleWidget = (id: string) => {
    setEnabledSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const animateAndMove = (
    id: string,
    direction: "up" | "down",
    index: number
  ) => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === widgetOrder.length - 1) return;

    setAnimatingId(id);
    setAnimatingDirection(direction);

    setTimeout(() => {
      setWidgetOrder((prev) => {
        const next = [...prev];
        const target = direction === "up" ? index - 1 : index + 1;
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
      setAnimatingId(null);
      setAnimatingDirection(null);
    }, 160);
  };

  const handleSave = () => {
    const orderedEnabled = widgetOrder.filter((id) => enabledSet.has(id));
    onSave({
      editingMode,
      postSignOutAction,
      widgetOrder: orderedEnabled,
      showQueueInSidebar,
    });
  };

  const labelMap = Object.fromEntries(
    ALL_REVIEW_WIDGETS.map((w) => [w.id, w.label])
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Preferences — Review Screen"
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "75vh",
          padding: 0,
        },
      }}
    >
      {/* Scrollable content */}
      <Stack
        gap="md"
        style={{ flex: 1, overflowY: "auto", padding: "16px 16px 0" }}
      >
        {/* Section 1: Editing Mode */}
        <div>
          <Text fw={600} size="sm" mb="xs" tt="uppercase">
            Editing Mode
          </Text>
          <Radio.Group
            value={editingMode}
            onChange={(v) => setEditingMode(v as "grid" | "document")}
          >
            <Stack gap="xs">
              <Radio value="grid" label="Table style entry" size="sm" />
              <Radio
                value="document"
                label="Word document style (edit report directly)"
                size="sm"
              />
            </Stack>
          </Radio.Group>
        </div>

        <Divider />

        {/* Section 2: Post Sign-Out Action */}
        <div>
          <Text fw={600} size="sm" mb="xs" tt="uppercase">
            Post Sign-Out Action
          </Text>
          <Radio.Group
            value={postSignOutAction}
            onChange={(v) =>
              setPostSignOutAction(
                v as "return-to-queue" | "next-case" | "scan-modal"
              )
            }
          >
            <Stack gap="xs">
              <Radio
                value="return-to-queue"
                label="Return to queue"
                size="sm"
              />
              <Radio
                value="next-case"
                label="Open next case in list"
                size="sm"
              />
              <Radio
                value="scan-modal"
                label="Show scan/enter modal for next case"
                size="sm"
              />
            </Stack>
          </Radio.Group>
        </div>

        <Divider />

        {/* Section 3: Left Sidebar Queue */}
        <Group justify="space-between" wrap="nowrap">
          <div>
            <Text fw={600} size="sm" tt="uppercase">
              Left Sidebar — Case Queue
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              Show the case queue below the navigation links
            </Text>
          </div>
          <Switch
            checked={showQueueInSidebar}
            onChange={(e) => setShowQueueInSidebar(e.currentTarget.checked)}
          />
        </Group>

        <Divider />

        {/* Section 4: Right Panel Widgets */}
        <div>
          <Text fw={600} size="sm" mb={4} tt="uppercase">
            Right Panel Widgets
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Drag to reorder or use the arrows. Toggle to show/hide.
          </Text>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={widgetOrder}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={0}>
                {widgetOrder.map((id, index) => (
                  <SortableWidgetRow
                    key={id}
                    id={id}
                    label={labelMap[id] ?? id}
                    checked={enabledSet.has(id)}
                    index={index}
                    total={widgetOrder.length}
                    animatingId={animatingId}
                    animatingDirection={animatingDirection}
                    onToggle={() => toggleWidget(id)}
                    onMoveUp={() => animateAndMove(id, "up", index)}
                    onMoveDown={() => animateAndMove(id, "down", index)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </div>
      </Stack>

      {/* Sticky footer */}
      <Group
        justify="space-between"
        p="md"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
          flexShrink: 0,
        }}
      >
        <Button variant="subtle" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isSaving}>
          Save Preferences
        </Button>
      </Group>
    </Modal>
  );
}
